import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { assetUrl } from "../../lib/api";
import { ExternalLink, Heart } from "lucide-react";

const PIN_RED = "#E60023";

/**
 * Trending Pins widget — social-proof block that surfaces what guests are saving.
 *
 * Why this approach (read me before "fixing")
 * -------------------------------------------
 * Pinterest deprecated their public `/v3/pidgets/domains/<host>/feed/` endpoint in 2018
 * and v5 requires OAuth that can only read YOUR OWN pins, not arbitrary domain saves.
 * So a truly live "pins from myecotour.in" feed is not technically possible without
 * standing up a Pinterest Business account + OAuth flow + a polling backend.
 *
 * What works publicly today, and what we use:
 *   1. pinterest.com/source/<domain> — Pinterest's own public page that lists every
 *      pin ever saved from a domain. We link to it as the "See all" target.
 *   2. We display 6 of our own 1000×1500 portrait OG images (the exact assets
 *      visitors pin via the Pin-it button) as a curated mood-board.
 *   3. Once an env-set Pinterest board URL exists, the headline gets a permanent
 *      "Follow board" CTA that uses Pinterest's official embed/follow protocol.
 */
const PINTEREST_DOMAIN = "myecotour.in";
const PINTEREST_SOURCE_URL = `https://pinterest.com/source/${PINTEREST_DOMAIN}`;

export default function TrendingPinsWidget() {
    const [picks, setPicks] = useState([]);

    useEffect(() => {
        let alive = true;
        // Pull a small curated set of featured destinations + experiences and route
        // their OG portrait endpoint as the visual. These are the exact 1000×1500
        // assets a visitor would pin via the Pin-it button — so the mood-board acts
        // as both social proof and content discovery.
        Promise.all([
            api.get("/destinations").then((r) => r.data || []),
            api.get("/experiences").then((r) => r.data || []),
        ])
            .then(([dests, exps]) => {
                if (!alive) return;
                const seed = [
                    ...dests.filter((d) => d.featured).slice(0, 4).map((d) => ({
                        kind: "destinations", id: d.id, title: d.name, path: `/destinations/${d.slug || d.id}`, locale: d.location,
                    })),
                    ...exps.filter((e) => e.featured).slice(0, 2).map((e) => ({
                        kind: "experiences", id: e.id, title: e.title, path: `/experiences/${e.slug || e.id}`, locale: e.duration,
                    })),
                ].slice(0, 6);
                setPicks(seed);
            })
            .catch(() => setPicks([]));
        return () => { alive = false; };
    }, []);

    const grid = useMemo(() => picks, [picks]);

    if (!grid.length) return null;

    return (
        <section className="py-20 bg-secondary/30 border-t border-border" data-testid="trending-pins-section">
            <div className="container-page">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                    <div className="max-w-xl">
                        <p className="overline mb-3 flex items-center gap-2" style={{ color: PIN_RED }}>
                            <PinterestIcon /> On Pinterest
                        </p>
                        <h2 className="font-serif text-3xl sm:text-4xl tracking-tight leading-tight">
                            Saved by travellers, <em className="font-serif italic text-accent">re-pinned by you.</em>
                        </h2>
                        <p className="mt-3 text-sm text-foreground/70 leading-relaxed">
                            A live mood-board of the journeys our community has been saving. Tap any tile to read the journey — or hit the Pin-it button on the page to add it to your own honeymoon, safari or sabbatical board.
                        </p>
                    </div>
                    <a
                        href={PINTEREST_SOURCE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="trending-pins-source-link"
                        className="inline-flex items-center gap-2 px-5 py-3 border border-border bg-background text-xs tracking-[0.22em] uppercase font-semibold hover:border-[color:var(--accent)] hover:text-accent transition-colors self-start"
                    >
                        See all on Pinterest <ExternalLink size={12} strokeWidth={1.5} />
                    </a>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4" data-testid="trending-pins-grid">
                    {grid.map((p, idx) => (
                        <Link
                            key={`${p.kind}-${p.id}`}
                            to={p.path}
                            data-testid={`trending-pin-${idx}`}
                            className="group block relative overflow-hidden bg-background border border-border hover:border-[color:var(--accent)] transition-colors"
                            style={{ aspectRatio: "2 / 3" }}
                            title={p.title}
                        >
                            <img
                                src={assetUrl(`/api/og/${p.kind}/${p.id}.jpg`)}
                                alt={`${p.title} — Pinterest portrait`}
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/75 via-black/30 to-transparent">
                                <p className="text-[10px] tracking-[0.18em] uppercase text-white/80 line-clamp-1">{p.locale || p.kind}</p>
                                <p className="text-xs sm:text-sm font-serif text-white leading-tight line-clamp-2">{p.title}</p>
                            </div>
                            <span className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/95 group-hover:bg-[color:var(--pin)] group-hover:text-white text-[color:var(--pin)] flex items-center justify-center transition-colors" style={{ "--pin": PIN_RED }} aria-hidden>
                                <PinterestIcon size={12} />
                            </span>
                        </Link>
                    ))}
                </div>

                <p className="mt-8 text-xs text-foreground/50 leading-relaxed text-center max-w-2xl mx-auto" data-testid="trending-pins-caption">
                    Each tile is a Pinterest-ready 1000×1500 portrait — auto-generated from the journey hero. <a href={PINTEREST_SOURCE_URL} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-accent">View saves from myecotour.in on Pinterest →</a>
                </p>
            </div>
        </section>
    );
}

function PinterestIcon({ size = 14 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12.001 0C5.376 0 0 5.376 0 12c0 5.084 3.163 9.422 7.625 11.166-.106-.949-.2-2.405.041-3.44.219-.937 1.406-5.964 1.406-5.964s-.359-.72-.359-1.781c0-1.668.967-2.914 2.171-2.914 1.024 0 1.519.769 1.519 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.6 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.137.893 2.739.098.119.112.223.083.345-.09.375-.293 1.199-.334 1.366-.053.219-.173.265-.402.16-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.624 0 12-5.376 12-12S18.624 0 12.001 0z" />
        </svg>
    );
}
